import React, { useCallback, useMemo } from 'react';
import { Navigate, Route, Routes, useNavigate, useParams } from "react-router-dom";
import { Box, Button } from "@mui/material";
import { Typography, useTheme } from "@mui/joy";
import useMediaQuery from "@mui/system/useMediaQuery";
import { useTranslation } from "react-i18next";
import useBreadcrumbs from "use-react-router-breadcrumbs";
import { useApolloClient, useQuery } from "@apollo/client";
import _ from "lodash";
import SystemDetailNavigation from "../../components/SystemDetailNavigation";
import SystemDashboard from "../../components/SystemDashboard";
import SystemFileManagement from "../../components/SystemFileManagement";
import SystemConditionMonitoringIframe from "../../components/SystemConditionMonitoringIframe";
import SystemDashboardActions from "../../components/SystemDashboardActions";
import AuthenticatedIframe from "../../components/AuthenticatedIframe";
import LegacyRouteRedirect from "../../components/LegacyRouteRedirect";
import Breadcrumbs, { breadcrumbConfig, BreadcrumbMatch } from "../../components/Breadcrumbs";
import { LoadingScreen } from '@dreebit-registry/vsm-microservice-components';
import { SystemIotConnection } from "../../graphql/queries/systemIotConnection";
import { SystemClientContract } from "../../graphql/queries/systemClientContract";
import { SystemBreadcrumbPath } from "../../graphql/queries/systemBreadcrumbPath";
import { isContractValid, isContractExpired, canViewIoT } from "../../utils/isContractValid";
import { useKeycloak } from "@react-keycloak/web";
import { getBreadcrumbPathNodeLink } from "../../utils/getBreadcrumbPathNodeLink";
import { isPlcSystemType } from "../../config/systemTypes";
import { isIoTAdminToken } from "../../utils/iotAttachGate";
import { useIotAttachGate } from "../../hooks/useIotAttachGate";
import { useCompactWidth } from "../../hooks/useCompactWidth";
import { parseIotSystemSavedMessage } from "../../utils/iotIframeMessages";

interface Props {
    /** Closes this system's tab after it was deleted (SystemRoutes.handleTabClose). */
    onSystemDeleted?: (systemId: string) => void;
}

const SystemDetailsScreen = ({ onSystemDeleted }: Props) => {
    const { systemId } = useParams<{ systemId: string }>();
    const theme = useTheme();
    const { t } = useTranslation();
    const { keycloak } = useKeycloak();
    const rawBreadcrumbs = useBreadcrumbs(breadcrumbConfig(t));

    const isIoTAdmin = useMemo(() => isIoTAdminToken(keycloak.token), [keycloak.token]);

    const systemPathQuery = useQuery(SystemBreadcrumbPath, {
        variables: { id: systemId },
        skip: !systemId,
    });

    const breadcrumbs = useMemo(() => {
        const pathNodes = _.get(systemPathQuery.data, 'system.installedBaseNode.pathNodes');
        if (!pathNodes?.length || pathNodes.length <= 1) return rawBreadcrumbs;

        const expanded: BreadcrumbMatch[] = [];
        for (const crumb of rawBreadcrumbs) {
            if (crumb.match.pathname === `/systems/${systemId}`) {
                // Add parent path nodes as clickable breadcrumb items (asset/system ancestors
                // open their dashboard, all others the installed base tree)
                pathNodes.slice(0, -1).forEach((node: any, i: number) => {
                    const pathIds = pathNodes.slice(0, i + 1).map((n: any) => n.id);
                    expanded.push({
                        match: crumb.match,
                        breadcrumb: node.title,
                        key: `pathnode-${node.id}`,
                        linkTo: getBreadcrumbPathNodeLink(node, pathIds),
                    });
                });
            }
            expanded.push(crumb);
        }
        return expanded;
    }, [rawBreadcrumbs, systemPathQuery.data, systemId]);

    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [showNavigation, setShowNavigation] = React.useState(false);
    const { ref: contentRef, compact } = useCompactWidth();

    const handleSwapView = () => {
        setShowNavigation(prev => !prev);
    };

    const systemIotConnectionQuery = useQuery(SystemIotConnection, {
        variables: { id: systemId },
        skip: !systemId,
    });
    const systemClientContractQuery = useQuery(SystemClientContract, {
        variables: { id: systemId },
        skip: !systemId,
    });

    const navigate = useNavigate();
    const apolloClient = useApolloClient();
    // IBM-1064: the IoT portal posts IOT_SYSTEM_SAVED after a create or edit in its iframe.
    // Refresh the IoT state (registration -> telemetry) and leave iotEdit for the dashboard,
    // so the next "Edit" mounts a fresh iframe instead of the portal's own post-save page.
    const handleIotIframeMessage = useCallback((data: unknown) => {
        const saved = parseIotSystemSavedMessage(data);
        if (!saved || !systemId) return;
        if (saved.systemId !== systemId) {
            console.warn('[IoT iframe] IOT_SYSTEM_SAVED for another system ignored:', saved.systemId);
            return;
        }
        apolloClient.refetchQueries({ include: ['SystemIotConnection', 'SystemNameplateInfo', 'SystemIoTConnectionStatus'] });
        navigate(`/systems/${systemId}/dashboard`);
    }, [apolloClient, navigate, systemId]);

    const contractData = _.get(systemClientContractQuery, 'data.system.client.contract');
    const hasIotConnection = !!_.get(systemIotConnectionQuery, 'data.system.iotConnection.id');
    const systemType = _.get(systemIotConnectionQuery, 'data.system.systemType') ?? null;
    const contractValid = isContractValid(contractData);
    const contractExpired = isContractExpired(contractData);
    // IoT admins may view existing data for an expired or missing contract; registration still needs a valid one.
    const canMonitorIoT = canViewIoT(contractData, isIoTAdmin);
    // Only PLC-backed systems are monitored via the external IoT-portal iframe; it renders
    // below the always-shown hero banner — telemetry when connected & the viewer may monitor,
    // device registration when a valid contract exists but no device yet.
    const isPlcSystem = isPlcSystemType(systemType);
    // Network scan gate (IBM-1039): registration needs a passed effective scan.
    // A blocked scan keeps the iframe away; the dashboard hero below explains it
    // (SystemDashboard). Not sent while the flag is off or once connected.
    const iotGate = useIotAttachGate({ kind: 'system', id: systemId, skip: hasIotConnection });
    const scanBlocked = iotGate.reason === 'networkScan';
    const showTelemetryIframe = isPlcSystem && hasIotConnection && canMonitorIoT;
    const showRegistrationIframe = isPlcSystem && contractValid && !hasIotConnection && !scanBlocked;
    const showIframe = showTelemetryIframe || showRegistrationIframe;

    if (!systemId) return null;

    return (
        <div className="flex-row" style={{ flexGrow: 1 }}>
            <Box
                sx={{
                    width: 320,
                    borderRight: "1px solid #ccc",
                    margin: "12px 0",
                    padding: "0 12px",
                    display: isMobile ? (showNavigation ? 'flex' : 'none') : 'flex',
                    flexDirection: 'column',
                }}
            >
                <SystemDetailNavigation
                    systemId={systemId}
                    systemType={systemType}
                    hasIotConnection={hasIotConnection}
                    isContractValid={contractValid}
                    isContractExpired={contractExpired}
                    isIoTAdmin={isIoTAdmin}
                />

                {isMobile && (
                    <Button onClick={handleSwapView} variant="outlined" sx={{ mb: 2 }}>
                        <Typography level="body-md">
                            {showNavigation ? t("Show Content") : t("Show Navigation")}
                        </Typography>
                    </Button>
                )}
            </Box>

            <Box
                sx={{
                    flex: 1,
                    padding: "12px 0 12px 12px",
                    width: "100%",
                    display: isMobile ? (showNavigation ? 'none' : 'flex') : 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                }}
            >
                {systemIotConnectionQuery.loading || systemClientContractQuery.loading || iotGate.pending ? <LoadingScreen /> : <>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ pb: 2 }}
                    >
                        <Breadcrumbs breadcrumbs={breadcrumbs} sx={{ flex: 1, minWidth: 0 }} />
                        <SystemDashboardActions systemId={systemId} onSystemDeleted={onSystemDeleted} hasIotConnection={hasIotConnection} />
                    </Box>

                    {isMobile && (
                        <Button onClick={handleSwapView} variant="outlined" sx={{ mb: 2 }}>
                            <Typography level="body-md">
                                {showNavigation ? t("Show Content") : t("Show Navigation")}
                            </Typography>
                        </Button>
                    )}

                    <Box ref={contentRef} sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                        <Routes>

                            <Route path="dashboard" element={
                                showIframe ? (
                                    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                                        <SystemDashboard systemId={systemId} systemType={systemType} hasIotConnection={hasIotConnection} iotConnectionLoading={false} isIoTAdmin={isIoTAdmin} compact={compact} heroOnly />
                                        <Box sx={{ flex: 1, minHeight: { xs: 420, md: 520 }, display: 'flex', flexDirection: 'column' }}>
                                            <SystemConditionMonitoringIframe systemId={systemId} page={showTelemetryIframe ? 'telemetry' : 'iot-connection'} onMessage={handleIotIframeMessage} />
                                        </Box>
                                    </Box>
                                ) : (
                                    <SystemDashboard systemId={systemId} systemType={systemType} hasIotConnection={hasIotConnection} iotConnectionLoading={false} isIoTAdmin={isIoTAdmin} compact={compact} />
                                )
                            } />
                            <Route path="files" element={<SystemFileManagement systemId={systemId} />} />
                            <Route path="maintenance" element={canMonitorIoT
                                ? <SystemConditionMonitoringIframe systemId={systemId} page="maintenance" />
                                : <Navigate to={`/systems/${systemId}/dashboard`} replace />} />
                            <Route path="service" element={canMonitorIoT
                                ? <SystemConditionMonitoringIframe systemId={systemId} page="service" />
                                : <Navigate to={`/systems/${systemId}/dashboard`} replace />} />
                            <Route path="alerts" element={canMonitorIoT
                                ? <SystemConditionMonitoringIframe systemId={systemId} page="notifications" />
                                : <Navigate to={`/systems/${systemId}/dashboard`} replace />} />
                            <Route path="iotEdit" element={<AuthenticatedIframe src={`${process.env.REACT_APP_BUSCH_IOT_URL}/iot-systems/iotSystemEdit/${systemId}`} title={t('Edit IoT System')} onMessage={handleIotIframeMessage} />} />
                            <Route path="iot-edit" element={<LegacyRouteRedirect />} />
                            <Route path="*" element={<Navigate to={`/systems/${systemId}/dashboard`} replace />} />
                        </Routes>
                    </Box>
                </>}
            </Box>
        </div>
    );
};

export default SystemDetailsScreen;
